import { autoinject, Container, Disposable } from 'aurelia-framework';
import { NavModel, Router } from 'aurelia-router';
import { EventAggregator } from 'aurelia-event-aggregator';
import { AppLogger } from 'app/shared/lib/logging/app-logger';

export enum PopoutState {
    Open = 1,
    Closed = 2
}

@autoinject()
export abstract class DemoContentPageBase {

    ////****************** Fields and Properties ************************************////
    protected router!: Router;
    protected readonly logger!: AppLogger;
    protected readonly events!: EventAggregator;

    protected currentRoute: string = '';
    protected helpLink: string | null = null;
    protected parentSubTitle: string = '';
    protected menuBarLabel: string = '';

    protected PopoutState = PopoutState;
    protected subMenuState: PopoutState = PopoutState.Closed;

    private navigationCompletedEvent: Disposable | null = null;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        router?: Router,
        events?: EventAggregator,
        logger?: AppLogger) {

        this.router = router as any;
        this.events = events as any;
        this.logger = logger || Container.instance.get(AppLogger);
        this.logger.source = this;
    }

    protected attached(): void {
        this.navigationCompletedEvent = this.events.subscribe('router:navigation:complete', () => this.navigationComplete());
        this.navigationComplete();
    }

    protected detached(): void {
        if(this.navigationCompletedEvent) { this.navigationCompletedEvent.dispose(); this.navigationCompletedEvent = null; }
    }


    ////****************** Events ***************************************************////
    protected navigationComplete(): void {
        const routeConfig = this.router.currentInstruction.config;
        const route = Array.isArray(routeConfig.route) ? routeConfig.route[0] : routeConfig.route;
        const parentRouteConfig = this.router.parent.currentInstruction.config;

        if(routeConfig && (routeConfig.nav)) {
            this.currentRoute = `/${route}`;
        }
        if(parentRouteConfig) {
            this.menuBarLabel = parentRouteConfig.title ?? '';
            this.parentSubTitle = this.menuBarLabel.replace('Demo: ', '');

        }
        this.helpLink = 'https://solid-fiesta-l8o7kv3.pages.github.io/';
        if(routeConfig.settings.helpFragment) { this.helpLink += routeConfig.settings.helpFragment; }
        this.subMenuState = PopoutState.Closed;  // Reset on navigation
    }


    ////****************** Functions ************************************************////
    protected navigationRoutes(): NavModel[] {
        return this.router.navigation.filter(route => {
            return !!route.config.settings.isVisible;
        });
    }

    protected toggleSubMenu(event: Event) {
        if(this.subMenuState === PopoutState.Closed) {
            this.openSubMenu(event);
        }
        else {
            this.closeSubMenu();
        }
    }


    protected openSubMenu(event: Event) {
        const target = event.currentTarget as HTMLElement;
        const subMenuElement = target.parentElement?.querySelector<HTMLElement>('menu');  //This should be a sibling to the interactive button
        if(!target || !subMenuElement) { return; }

        this.subMenuState = PopoutState.Open;
        this.updateDropdownPosition(target, subMenuElement, this.subMenuState);
    }

    public closeSubMenu() {
        this.subMenuState = PopoutState.Closed;
    }

    protected updateDropdownPosition(target: HTMLElement, dropdownContainer: HTMLElement, dropdownState: PopoutState) {
        if(dropdownState === PopoutState.Open) {
            const { y, width, height, left, top, bottom } = target.getBoundingClientRect();
            const opensUp = y > window.innerHeight / 2;
            const opensRight = left + dropdownContainer.clientWidth > window.innerWidth;
            dropdownContainer.style.setProperty('left', opensRight ? `calc(100vw - ${(left + dropdownContainer.clientWidth - window.innerWidth)}px)` : `${left}px`);
            dropdownContainer.style.setProperty('top', opensUp ? '' : `${(top + height)}px`);
            dropdownContainer.style.setProperty('bottom', opensUp ? `calc(100vh - ${bottom}px)` : '');
        }
    };

}
